import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { printElement } from "../../utils/printTable";
import {
    listBranches,
    getRbiNbfcCompliance,
    getauditlogs,
    getauditstatistics,
} from "../../services/admin";

/**
 * RBI / NBFC Compliance & Audit Trail Report
 *
 * Brings the prudential portfolio metrics (used for RBI/NBFC-MFI regulatory
 * returns) together with the system audit trail in a single, printable report.
 *
 * Compliance items are split into two kinds:
 *  - Auto-computed   -> derived from live portfolio / audit data.
 *  - Manual / Policy -> require verification at onboarding or are governed by
 *                       company policy; flagged clearly so they are not mistaken
 *                       for system-verified facts.
 */
export default function RbiNbfcCompliance() {
    const [activeTab, setActiveTab] = useState("compliance"); // compliance | audit | statistics
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const reportRef = useRef(null);

    const [compliance, setCompliance] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditStats, setAuditStats] = useState(null);

    const [filters, setFilters] = useState({
        branch_id: "",
        date_from: moment().startOf("month").format("YYYY-MM-DD"),
        date_to: moment().format("YYYY-MM-DD"),
    });

    useEffect(() => {
        loadBranches();
        generateReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadBranches = () => {
        listBranches().then((res) => {
            if (res && res.status === true) {
                setBranches(res.data || []);
            }
        });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const generateReport = () => {
        setLoading(true);
        const compParams = { date_to: filters.date_to };
        if (filters.branch_id) compParams.branch_id = filters.branch_id;

        Promise.all([
            getRbiNbfcCompliance(compParams),
            getauditlogs({
                start_date: filters.date_from,
                end_date: filters.date_to,
                limit: 200,
            }),
            getauditstatistics({
                start_date: filters.date_from,
                end_date: filters.date_to,
            }),
        ])
            .then(([compRes, logsRes, statsRes]) => {
                if (compRes && compRes.status === true) {
                    setCompliance(compRes.data);
                }
                if (logsRes && logsRes.status === true) {
                    setAuditLogs(logsRes.data || []);
                }
                if (statsRes && statsRes.status === true) {
                    setAuditStats(statsRes.data);
                }
            })
            .catch(() => {
                toast.error("Failed to generate compliance report");
            })
            .finally(() => setLoading(false));
    };

    const inr = (value) =>
        "₹" +
        parseFloat(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

    const num = (value) => parseInt(value || 0, 10).toLocaleString("en-IN");

    // ---- Prudential metrics (computed server-side) ----------------------
    const portfolioData = compliance?.portfolio || {};
    const memberSummary = compliance?.members || {};
    const totalLoans = parseInt(portfolioData.total_loans || 0, 10);
    const overdueLoans = parseInt(portfolioData.overdue_loans || 0, 10);
    const activeLoans = parseInt(portfolioData.active_loans || 0, 10);
    const closedLoans = parseInt(portfolioData.closed_loans || 0, 10);
    const grossLoanPortfolio = parseFloat(portfolioData.gross_loan_portfolio || 0);
    const totalDisbursed = parseFloat(portfolioData.total_disbursed || 0);
    const npaRatio = parseFloat(portfolioData.npa_ratio || 0);
    const standardAccounts = parseInt(
        portfolioData.standard_accounts ?? Math.max(totalLoans - overdueLoans, 0),
        10
    );

    const statusLabel = portfolioData.npa_status || (npaRatio < 3 ? "Good" : npaRatio <= 5 ? "Watch" : "High");
    const npaStatus = {
        label: statusLabel,
        cls: statusLabel === "Good" ? "bg-success" : statusLabel === "Watch" ? "bg-warning" : "bg-danger",
    };

    const incomeDataAvailable = parseInt(memberSummary.income_data_available || 0, 10);
    const incomeExceeding = parseInt(memberSummary.income_exceeding_limit || 0, 10);
    const kycVerified = parseInt(memberSummary.kyc_verified || 0, 10);

    const auditTrailMaintained =
        compliance?.audit?.maintained ?? (auditStats?.total_logs ?? auditLogs.length) > 0;

    // ---- RBI / NBFC-MFI compliance checklist ----------------------------
    // Reference: RBI Master Direction – Regulatory Framework for Microfinance
    // Loans, 2022 (NBFC-MFI norms) + prudential asset-classification norms.
    const checklist = [
        {
            norm: "Asset classification (90+ DPD treated as NPA)",
            kind: "Auto",
            value: `${num(overdueLoans)} overdue of ${num(totalLoans)} accounts`,
            status: npaStatus.label,
            cls: npaStatus.cls,
            remarks: "Overdue / non-performing accounts as per portfolio status.",
        },
        {
            norm: "Gross NPA ratio within prudential expectation",
            kind: "Auto",
            value: `${npaRatio.toFixed(2)}%`,
            status: npaStatus.label,
            cls: npaStatus.cls,
            remarks: "GNPA% = overdue accounts / total accounts.",
        },
        {
            norm: "Minimum 75% qualifying (microfinance) assets",
            kind: "Manual",
            value: "—",
            status: "Verify",
            cls: "bg-secondary",
            remarks:
                "Net-asset composition to be confirmed against balance sheet for the NBFC-MFI threshold.",
        },
        {
            norm: "Collateral-free lending",
            kind: "Policy",
            value: "Enforced",
            status: "Compliant",
            cls: "bg-success",
            remarks: "Microfinance loans disbursed without collateral as per policy.",
        },
        {
            norm: "No pre-payment penalty",
            kind: "Policy",
            value: "Enforced",
            status: "Compliant",
            cls: "bg-success",
            remarks: "Foreclosure permitted without penalty.",
        },
        {
            norm: "Household annual income ≤ ₹3,00,000",
            kind: "Auto",
            value:
                incomeDataAvailable > 0
                    ? `${num(incomeExceeding)} breach(es) / ${num(incomeDataAvailable)} captured`
                    : "No income data",
            status: incomeDataAvailable === 0 ? "Verify" : incomeExceeding === 0 ? "Compliant" : "Attention",
            cls: incomeDataAvailable === 0 ? "bg-secondary" : incomeExceeding === 0 ? "bg-success" : "bg-danger",
            remarks:
                "Members with recorded annual income above the ₹3.0L microfinance limit. Capture income for all members for full coverage.",
        },
        {
            norm: "KYC verification of members",
            kind: "Auto",
            value: `${num(kycVerified)} verified / ${num(memberSummary.total_members)}`,
            status: kycVerified >= (memberSummary.total_members || 0) && (memberSummary.total_members || 0) > 0 ? "Compliant" : "Attention",
            cls: kycVerified >= (memberSummary.total_members || 0) && (memberSummary.total_members || 0) > 0 ? "bg-success" : "bg-warning",
            remarks: "Members whose KYC status is verified/approved (Aadhaar, PAN, bank).",
        },
        {
            norm: "Monthly repayment obligation ≤ 50% of household income",
            kind: "Manual",
            value: "At onboarding",
            status: "Verify",
            cls: "bg-secondary",
            remarks: "Indebtedness cap assessed before sanction.",
        },
        {
            norm: "Fair Practices Code & pricing disclosure",
            kind: "Policy",
            value: "Disclosed",
            status: "Compliant",
            cls: "bg-success",
            remarks: "Effective interest rate and factsheet disclosed to borrowers.",
        },
        {
            norm: "Audit trail maintained for transactions",
            kind: "Auto",
            value: auditTrailMaintained
                ? `${num(auditStats?.total_logs ?? auditLogs.length)} log(s)`
                : "No logs",
            status: auditTrailMaintained ? "Compliant" : "Attention",
            cls: auditTrailMaintained ? "bg-success" : "bg-danger",
            remarks: "System-generated audit log of create/update/delete actions.",
        },
    ];

    const getActionBadge = (action) => {
        const badges = {
            Created: "bg-success",
            Updated: "bg-primary",
            Deleted: "bg-danger",
            Posted: "bg-info",
            Approved: "bg-success",
            Cancelled: "bg-warning",
            Closed: "bg-secondary",
        };
        return <span className={`badge ${badges[action] || "bg-secondary"}`}>{action}</span>;
    };

    // window.print() printed the whole dashboard around the report, so only the
    // report section is sent to the printer.
    const handlePrint = () => {
        const tabTitle =
            activeTab === "audit" ? "Audit Trail" : activeTab === "statistics" ? "Audit Statistics" : "Compliance";

        printElement(reportRef.current, {
            title: `RBI / NBFC ${tabTitle} Report`,
            orientation: "landscape"
        });
    };

    const exportAuditCsv = () => {
        if (!auditLogs.length) {
            toast.info("No audit logs to export");
            return;
        }
        const headers = ["Date/Time", "User", "Action", "Module", "Record Type", "Record", "Description", "IP Address"];
        const rows = auditLogs.map((log) => [
            moment(log.created_at).format("YYYY-MM-DD HH:mm:ss"),
            log.user?.name || "Unknown",
            log.action,
            log.module,
            log.record_type,
            log.record_identifier,
            (log.description || "").replace(/"/g, "'"),
            log.ip_address,
        ]);
        const csv = [headers, ...rows]
            .map((r) => r.map((c) => `"${c ?? ""}"`).join(","))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `audit-trail_${filters.date_from}_to_${filters.date_to}.csv`;
        link.click();
    };

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">RBI / NBFC Compliance & Audit Trail</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Reports</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        RBI / NBFC Compliance &amp; Audit Trail
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
                            <h4 className="card-title mb-0">
                                <i className="fa fa-shield"></i> RBI / NBFC Compliance &amp; Audit Trail Report
                            </h4>
                            <div className="text-end">
                                <button
                                    className={`btn btn-sm ${activeTab === "compliance" ? "btn-primary" : "btn-outline-primary"} me-1`}
                                    onClick={() => setActiveTab("compliance")}
                                >
                                    <i className="fa fa-check-square-o"></i> Compliance
                                </button>
                                <button
                                    className={`btn btn-sm ${activeTab === "audit" ? "btn-primary" : "btn-outline-primary"} me-1`}
                                    onClick={() => setActiveTab("audit")}
                                >
                                    <i className="fa fa-list"></i> Audit Trail
                                </button>
                                <button
                                    className={`btn btn-sm ${activeTab === "statistics" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => setActiveTab("statistics")}
                                >
                                    <i className="fa fa-bar-chart"></i> Statistics
                                </button>
                            </div>
                        </div>

                        <div className="card-body">
                            {/* Filters */}
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Branch</label>
                                    <select
                                        className="form-select form-control"
                                        name="branch_id"
                                        value={filters.branch_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Branches</option>
                                        {branches.map((branch) => (
                                            <option key={branch.id} value={branch.id}>
                                                {branch.branch_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label fw-bold">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="date_from"
                                        value={filters.date_from}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label fw-bold">To Date (As on)</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="date_to"
                                        value={filters.date_to}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                                <div className="col-md-5 d-flex align-items-end">
                                    <button className="btn btn-primary me-2" onClick={generateReport} disabled={loading}>
                                        <i className="fa fa-cogs"></i> {loading ? "Generating..." : "Generate"}
                                    </button>
                                    <button className="btn btn-outline-secondary me-2" onClick={handlePrint}>
                                        <i className="fa fa-print"></i> Print
                                    </button>
                                    <button className="btn btn-outline-success" onClick={exportAuditCsv}>
                                        <i className="fa fa-file-excel-o"></i> Export Audit CSV
                                    </button>
                                </div>
                            </div>

                            <div ref={reportRef}>
                            {/* Compliance Tab */}
                            {activeTab === "compliance" && (
                                <>
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6>Gross Loan Portfolio</h6>
                                                    <h4 className="text-primary">{inr(grossLoanPortfolio)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6>Total Disbursed</h6>
                                                    <h4 className="text-success">{inr(totalDisbursed)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6>Gross NPA Ratio</h6>
                                                    <h4>
                                                        {npaRatio.toFixed(2)}%{" "}
                                                        <span className={`badge ${npaStatus.cls} align-middle`}>{npaStatus.label}</span>
                                                    </h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6>Active Members</h6>
                                                    <h4 className="text-info">{num(memberSummary?.active_members)}</h4>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Asset classification */}
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <div className="card">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">Asset Classification (as on {moment(filters.date_to).format("DD-MMM-YYYY")})</h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row text-center">
                                                        <div className="col">
                                                            <h6 className="text-muted">Total Accounts</h6>
                                                            <h4>{num(totalLoans)}</h4>
                                                        </div>
                                                        <div className="col">
                                                            <h6 className="text-muted">Standard</h6>
                                                            <h4 className="text-success">{num(standardAccounts)}</h4>
                                                        </div>
                                                        <div className="col">
                                                            <h6 className="text-muted">Overdue / NPA</h6>
                                                            <h4 className="text-danger">{num(overdueLoans)}</h4>
                                                        </div>
                                                        <div className="col">
                                                            <h6 className="text-muted">Active</h6>
                                                            <h4 className="text-primary">{num(activeLoans)}</h4>
                                                        </div>
                                                        <div className="col">
                                                            <h6 className="text-muted">Closed</h6>
                                                            <h4 className="text-secondary">{num(closedLoans)}</h4>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Compliance checklist */}
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th width="30%">RBI / NBFC-MFI Norm</th>
                                                    <th width="10%">Type</th>
                                                    <th width="15%">Value</th>
                                                    <th width="12%">Status</th>
                                                    <th>Remarks</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {checklist.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.norm}</td>
                                                        <td>
                                                            <span
                                                                className={`badge ${
                                                                    item.kind === "Auto"
                                                                        ? "bg-info"
                                                                        : item.kind === "Policy"
                                                                        ? "bg-primary"
                                                                        : "bg-secondary"
                                                                }`}
                                                            >
                                                                {item.kind}
                                                            </span>
                                                        </td>
                                                        <td>{item.value}</td>
                                                        <td>
                                                            <span className={`badge ${item.cls}`}>{item.status}</span>
                                                        </td>
                                                        <td>
                                                            <small>{item.remarks}</small>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="alert alert-info">
                                        <small>
                                            <strong>Note:</strong> <span className="badge bg-info">Auto</span> items are
                                            derived from live data. <span className="badge bg-secondary">Manual</span> items
                                            require verification at onboarding/KYC, and <span className="badge bg-primary">Policy</span>{" "}
                                            items are governed by company policy. This report is an internal compliance aid and
                                            does not replace the statutory RBI returns.
                                        </small>
                                    </div>
                                </>
                            )}

                            {/* Audit Trail Tab */}
                            {activeTab === "audit" && (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th width="13%">Date/Time</th>
                                                <th width="10%">User</th>
                                                <th width="8%">Action</th>
                                                <th width="10%">Module</th>
                                                <th width="12%">Record Type</th>
                                                <th width="15%">Record</th>
                                                <th>Description</th>
                                                <th width="10%">IP Address</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {auditLogs.length > 0 ? (
                                                auditLogs.map((log) => (
                                                    <tr key={log.id}>
                                                        <td>
                                                            <small>{moment(log.created_at).format("DD-MM-YYYY HH:mm:ss")}</small>
                                                        </td>
                                                        <td>{log.user?.name || "Unknown"}</td>
                                                        <td>{getActionBadge(log.action)}</td>
                                                        <td>
                                                            <small>{log.module}</small>
                                                        </td>
                                                        <td>
                                                            <small>{log.record_type}</small>
                                                        </td>
                                                        <td>
                                                            <strong>{log.record_identifier}</strong>
                                                        </td>
                                                        <td>
                                                            <small>{log.description}</small>
                                                        </td>
                                                        <td>
                                                            <small>{log.ip_address}</small>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted">
                                                        {loading ? "Loading..." : "No audit logs found for the selected period."}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {auditLogs.length > 0 && (
                                        <div className="alert alert-info">
                                            <small>Showing {auditLogs.length} audit log(s) for the selected date range.</small>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Statistics Tab */}
                            {activeTab === "statistics" && auditStats && (
                                <>
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <div className="card bg-primary text-white">
                                                <div className="card-body">
                                                    <h6>Total Audit Logs</h6>
                                                    <h2>{num(auditStats.total_logs)}</h2>
                                                    <small>In selected date range</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="card bg-info text-white">
                                                <div className="card-body">
                                                    <h6>Active Users</h6>
                                                    <h2>{num(auditStats.total_users)}</h2>
                                                    <small>Users who performed actions</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="card">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">Actions Breakdown</h6>
                                                </div>
                                                <div className="card-body">
                                                    <table className="table table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Action</th>
                                                                <th className="text-end">Count</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Object.entries(auditStats.by_action || {}).map(([action, count]) => (
                                                                <tr key={action}>
                                                                    <td>{getActionBadge(action)}</td>
                                                                    <td className="text-end">
                                                                        <strong>{count}</strong>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="card">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">Modules Breakdown</h6>
                                                </div>
                                                <div className="card-body">
                                                    <table className="table table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Module</th>
                                                                <th className="text-end">Count</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {Object.entries(auditStats.by_module || {}).map(([module, count]) => (
                                                                <tr key={module}>
                                                                    <td>{module}</td>
                                                                    <td className="text-end">
                                                                        <strong>{count}</strong>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === "statistics" && !auditStats && (
                                <div className="text-center text-muted py-5">
                                    <i className="fa fa-info-circle fa-2x mb-2"></i>
                                    <p>No statistics available for the selected period.</p>
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
